import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Trophy, Star, Target, Award, Home, Calculator, Users, Copy, Check } from 'lucide-react';
import { useGame } from '../context/GameContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileDashboard = () => {
  const navigate = useNavigate();
  const { playerName, totalScore, gamesPlayed, achievements } = useGame();
  const [linkCode, setLinkCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Fetch the child's link code
    const fetchLinkCode = async () => {
      try {
        const response = await axios.get(`${API}/player/${playerName}/link-code`);
        if (response.data.link_code) {
          setLinkCode(response.data.link_code);
        }
      } catch (error) {
        console.error('Error fetching link code:', error);
      }
    };
    if (playerName) {
      fetchLinkCode();
    }
  }, [playerName]);

  const copyLinkCode = () => {
    if (linkCode) {
      navigator.clipboard.writeText(linkCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = linkCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const stats = [
    { label: 'Total Score', value: totalScore, icon: Trophy, color: 'from-yellow-400 to-orange-400' },
    { label: 'Games Played', value: gamesPlayed, icon: Target, color: 'from-blue-400 to-cyan-400' },
    { label: 'Achievements', value: achievements.length, icon: Award, color: 'from-purple-400 to-pink-400' },
    { label: 'Level', value: Math.floor(totalScore / 100) + 1, icon: Star, color: 'from-green-400 to-emerald-400' }
  ];

  const levelProgress = (totalScore % 100);

  const mockAchievements = [
    { id: 1, name: 'First Steps', description: 'Completed your first game!', icon: Star, unlocked: gamesPlayed >= 1 },
    { id: 2, name: 'Quick Learner', description: 'Played 5 games', icon: Trophy, unlocked: gamesPlayed >= 5 },
    { id: 3, name: 'Math Master', description: 'Scored 100 points in Math', icon: Calculator, unlocked: totalScore >= 100 },
    { id: 4, name: 'Dedicated Student', description: 'Played 10 games', icon: Award, unlocked: gamesPlayed >= 10 }
  ];

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)'
    }}>
      <div className="max-w-6xl mx-auto">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-110 transition-transform"
        >
          <Home className="w-5 h-5 mr-2" />
          Home
        </Button>

        <div className="text-center mb-8 bounce-in">
          <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
            <span className="text-5xl font-bold text-white">{playerName[0].toUpperCase()}</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-2">{playerName}'s Profile</h1>
          <p className="text-2xl text-gray-700">Keep up the great work!</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card
                key={index}
                className="p-6 text-center slide-up"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${stat.color.replace('from-', '').replace('to-', '')})`
                }}
              >
                <IconComponent className="w-12 h-12 mx-auto mb-2 text-gray-800" />
                <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                <p className="text-sm text-gray-800 font-semibold">{stat.label}</p>
              </Card>
            );
          })}
        </div>

        <Card className="p-8 mb-8 bg-white/95" style={{ borderRadius: '24px' }}>
          <h2 className="text-3xl font-bold text-gray-800 mb-4 flex items-center">
            <Star className="w-8 h-8 mr-3 text-yellow-500" />
            Level Progress
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between text-lg">
              <span className="font-semibold text-gray-700">Level {Math.floor(totalScore / 100) + 1}</span>
              <span className="font-semibold text-gray-700">Level {Math.floor(totalScore / 100) + 2}</span>
            </div>
            <Progress value={levelProgress} className="h-6" />
            <p className="text-center text-gray-600 mt-2">{levelProgress}% to next level!</p>
          </div>
        </Card>

        {/* Parent Link Code Section */}
        {linkCode && (
          <Card className="p-8 mb-8 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200" style={{ borderRadius: '24px' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Users className="w-7 h-7 mr-3 text-blue-500" />
              Share With Your Parent! 👨‍👩‍👧
            </h2>
            <p className="text-gray-600 mb-4">
              Give this special code to your parent so they can see your progress!
            </p>
            <div className="flex items-center justify-center gap-4 p-4 bg-white rounded-2xl border-2 border-dashed border-blue-300">
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-1">Your Secret Code</p>
                <p className="text-4xl font-bold tracking-widest text-blue-600 font-mono">
                  {linkCode}
                </p>
              </div>
              <Button
                onClick={copyLinkCode}
                className={`px-4 py-2 transition-all ${copied ? 'bg-green-500' : 'bg-blue-500'} text-white rounded-xl`}
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <p className="text-center text-sm text-gray-500 mt-3">
              🔒 Only share this with your parent or teacher!
            </p>
          </Card>
        )}

        <Card className="p-8 bg-white/95" style={{ borderRadius: '24px' }}>
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <Award className="w-8 h-8 mr-3 text-purple-500" />
            Achievements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {mockAchievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <Card
                  key={achievement.id}
                  className={`p-6 transition-all duration-300 ${achievement.unlocked ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-300' : 'bg-gray-100 opacity-50'}`}
                  style={{ borderRadius: '16px' }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-full ${achievement.unlocked ? 'bg-yellow-400' : 'bg-gray-300'}`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{achievement.name}</h3>
                      <p className="text-gray-600">{achievement.description}</p>
                      {achievement.unlocked && (
                        <p className="text-sm text-green-600 font-semibold mt-2">Unlocked!</p>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </Card>

        <div className="text-center mt-8 space-y-4">
          <Button
            onClick={() => navigate('/badges')}
            size="lg"
            className="w-full max-w-md px-12 py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: '16px'
            }}
          >
            <Award className="w-6 h-6 mr-2" />
            View All Badges
          </Button>
          <Button
            onClick={() => navigate('/age-selection')}
            size="lg"
            className="w-full max-w-md px-12 py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px'
            }}
          >
            Play More Games
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileDashboard;
